"use client";
import React from "react";
import { Pagination as BSPagination } from "react-bootstrap";
import styles from "./BlogCard.module.css";

interface Props {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const buildPageWindow = (
  current: number,
  total: number,
  windowSize: number
): (number | "ellipsis")[] => {
  if (total <= windowSize + 4) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | "ellipsis")[] = [];
  const half = Math.floor(windowSize / 2);
  let start = Math.max(2, current - half);
  let end = Math.min(total - 1, current + half);

  if (current <= half + 2) {
    start = 2;
    end = 1 + windowSize;
  } else if (current >= total - (half + 1)) {
    start = total - windowSize;
    end = total - 1;
  }

  pages.push(1);
  if (start > 2) {
    pages.push("ellipsis");
  }

  for (let i = start; i <= end; i += 1) {
    pages.push(i);
  }

  if (end < total - 1) {
    pages.push("ellipsis");
  }
  pages.push(total);

  return pages;
};

export const Pagination: React.FC<Props> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const pageWindow = buildPageWindow(currentPage, totalPages, 5);

  const handleChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  return (
    <div className="d-flex flex-column align-items-center gap-2">
      <BSPagination className={styles.pagination}>
        <BSPagination.First
          onClick={() => handleChange(1)}
          disabled={currentPage === 1}
        />
        <BSPagination.Prev
          onClick={() => handleChange(currentPage - 1)}
          disabled={currentPage === 1}
        />

        {pageWindow.map((item, index) => {
          if (item === "ellipsis") {
            return (
              <BSPagination.Ellipsis key={`ellipsis-${index}`} disabled />
            );
          }

          return (
            <BSPagination.Item
              key={item}
              active={item === currentPage}
              onClick={() => handleChange(item)}
              className={styles.pageItem}
            >
              {item}
            </BSPagination.Item>
          );
        })}

        <BSPagination.Next
          onClick={() => handleChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        />
        <BSPagination.Last
          onClick={() => handleChange(totalPages)}
          disabled={currentPage === totalPages}
        />
      </BSPagination>
      <div className="text-muted small">
        Page {currentPage} of {totalPages}
      </div>
    </div>
  );
};
